import React, { useEffect, useState } from 'react';
import styles from './MediaAdd.module.css';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import FileUploader from './FileUploader/FileUploader';
import useBackdrop from '../../hooks/useBackdrop/useBackdrop';
import { Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const formSchema = yup.object({
    category: yup.string().required("Category is Required"),
    subCategory: yup.string().required("Sub Category is Required"),
    title: yup.string().required("Title is Required"),
    link: yup.string().required("Link is Required"),
    description: yup.string().required("Description is Required")
});

const MediaAdd = () => {
    const { id } = useParams();



    const [initialValues, setInitailValues] = useState({ category: "", subCategory: "", title: "", description: "", link: "" });
    const [categoryData, setCategoryData] = useState([]);
    const [subCategoryData, setSubCategoryData] = useState([]);
    const [categoryToggle, setCategoryToggle] = useState(false);
    const [subCategoryToggle, setsubCategoryToggle] = useState(false);
    const { openBackdrop, closeBackdrop, BackdropComponent } = useBackdrop();
    const [img, setImg] = useState(null);

    // helper (kept for optional use): convert remote URL to File (requires CORS on remote)
    const urlToFile = async (url) => {
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blob = response.data;
            return new File([blob], "photo.jpg", { type: blob.type });
        } catch (error) {
            console.warn(`urlToFile failed for ${url}:`, error.message);
            throw error;
        }
    };

    const handleImgUpdate = (data) => {
        setImg(data);
    };

    const navigate = useNavigate();

    const formSubmit = async (values, { setSubmitting, resetForm }) => {
        openBackdrop();
        setSubmitting(true);
        try {
            let payload = { ...values };

            if (categoryToggle) {
                const categoryName = categoryData.find((item) => item.id === parseInt(values.category)).name;
                payload = { ...payload, category: categoryName };
            }

            if (subCategoryToggle) {
                const subCategoryName = subCategoryData.find((item) => item.id === parseInt(values.subCategory)).name;
                payload = { ...payload, subCategory: subCategoryName };
            }

            const URL = id ? `${process.env.REACT_APP_COSMOSADMIN}/meta/updateArticle/${id}` : `${process.env.REACT_APP_COSMOSADMIN}/meta/addArticle`;

            const res = await axios.post(URL, payload);

            const articleId = id ? id : (res && res.data && res.data.articleId);

            if (articleId && img && typeof img !== 'string') {
                const formImageData = new FormData();
                formImageData.append('file', img);
                await axios.post(`${process.env.REACT_APP_COSMOSADMIN}/meta/uploadArticleImage/${articleId}`, formImageData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            // successful save — reset and navigate back without browser alert
            resetForm();
            setImg(null);
            navigate(-1);
        } catch (error) {
            // log the error for debugging but don't show a blocking alert to the user
            console.error('MediaAdd formSubmit error:', error);
        } finally {
            setSubmitting(false);
            closeBackdrop();
        }
    };

    const getCategoryData = async () => {
        try {
            const res = await axios(`${process.env.REACT_APP_COSMOSADMIN}/meta/getAllNewsCategories`);
            const { data } = res;
            setCategoryData(data);
            if (data.length > 0) {
                setCategoryToggle(true);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getSubCategoryData = async (id) => {
        try {
            const res = await axios(`${process.env.REACT_APP_COSMOSADMIN}/meta/getAllNewsSubCategories/${id}`);
            const { data } = res;
            setSubCategoryData(data);
            if (data.length > 0) {
                setsubCategoryToggle(true);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchPostDetails = async (id) => {
        try {
            const res = await axios(`${process.env.REACT_APP_COSMOSADMIN}/meta/getArticle/${id}`);
            const post = res.data;
            setCategoryToggle(false);
            setsubCategoryToggle(false)

            setInitailValues({
                title: post.title,
                category: post.category,
                subCategory: post.subCategory,
                link: post.link,
                description: post.description
            });
                // API returns image URL in `post.url` (example: /meta/imageDownload/...).
                // Set `img` to the URL so FileUploader can show the existing image.
                if (post.url) {
                    setImg(post.url);
            }
        } catch (error) {
            console.error("Error fetching post details:", error);
        } finally {
            closeBackdrop();
        }
    };

    // We intentionally run this effect when `id` changes. The functions used inside
    // (openBackdrop/closeBackdrop/getCategoryData/fetchPostDetails) are stable enough
    // for our usage here; disabling the exhaustive-deps rule avoids rerunning on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const getData = async () => {
            openBackdrop();
            await getCategoryData();
            if (id) {
                await fetchPostDetails(id);
            }
            closeBackdrop();
        };

        getData();
    }, [id]);

    return (
        <>
            <BackdropComponent />
            <div className={styles.container}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={formSchema}
                    onSubmit={formSubmit}
                    enableReinitialize={true}
                >
                    {({ isSubmitting, values, handleChange, handleBlur, setFieldValue }) => {

                        const getLinkData = async () => {
                            openBackdrop();
                            if (values.link) {
                                try {
                                    const res = await axios.get(`${process.env.REACT_APP_COSMOSADMIN}/meta/preview?url=${values.link}`);
                                    const { data } = res;
                                    setFieldValue('title', data.title);
                                    setFieldValue('description', data.description);
                                    const img = await urlToFile(data.image);
                                    setImg(img);
                                } catch (error) {
                                    console.log(error);
                                }
                            }
                            closeBackdrop();
                        };

                        return (
                            <Form style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
                                <div className={styles.formContainer}>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="category">Category</label>
                                        <div className={styles.inputContainer}>
                                            {
                                                categoryToggle ?
                                                    <select name="category" onChange={(e) => { handleChange(e); getSubCategoryData(e.target.value); }}
                                                        onBlur={handleBlur}
                                                        value={values.category}>
                                                        <option value="">Select Category</option>
                                                        {
                                                            categoryData.map((item) => (
                                                                <option key={item.id} value={item.id}>{item.name}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    : <Field type="text" placeholder='Category' name='category' />
                                            }
                                            <span className={styles.toggleButton} onClick={() => setCategoryToggle(prev => !prev)}><AddIcon /></span>
                                        </div>
                                        <ErrorMessage name='category' component="span" className='' />
                                    </div>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="subCategory">Sub Category</label>
                                        <div className={styles.inputContainer}>
                                            {
                                                subCategoryToggle ?
                                                    <select name="subCategory" onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={values.subCategory}>
                                                        <option value="">Select Sub Category</option>
                                                        {
                                                            subCategoryData.map((item) => (
                                                                <option key={item.id} value={item.id}>{item.name}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    : <Field type="text" placeholder='Sub-Category' name='subCategory' />
                                            }
                                            <span className={styles.toggleButton} onClick={() => setsubCategoryToggle(prev => !prev)}><AddIcon /></span>
                                        </div>
                                        <ErrorMessage name='subCategory' component="span" className='' />
                                    </div>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="link">Link</label>
                                        <Field type="text" placeholder='Link to Post' name='link' onBlur={(e) => { handleBlur(e); getLinkData(); }} />
                                        <ErrorMessage name='link' component="span" className='' />
                                    </div>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="title">Title</label>
                                        <Field type="text" placeholder='Title' name='title' />
                                        <ErrorMessage name='title' component="span" className='' />
                                    </div>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="description">Description</label>
                                        <Field type="text" placeholder='Description' name='description' />
                                        <ErrorMessage name='description' component="span" className='' />
                                    </div>
                                    <div className={styles.inputWrapper}>
                                        <label htmlFor="description">Image</label>
                                        <FileUploader onChange={handleImgUpdate} value={img} />
                                    </div>
                                </div>
                                <div>
                                    <Button type='submit' className={styles.btnClass} variant='contained' disabled={isSubmitting}>
                                        Submit
                                    </Button>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </>
    );
};

export default MediaAdd;
